import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import {
    Box, List, CssBaseline, Typography,
    IconButton, ListItem, ListItemIcon, ListItemText,
    ListSubheader, Drawer as MuiDrawer
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/reducers/index";
import { setUnsavedLotChanges } from "../redux/actions/index";

import useWindowDimensions from "../tools/useWindowDimensions";
import DrawerHeader from "./DrawerHeader";
import Navbar from "./Navbar";
import AlerteDialog from "../components/AlerteDialog/AlerteDialog";

const drawerWidth = 280;

export interface MenuItem {
    icon: React.ReactNode;
    text: string;
    page: string;
}

export interface MenuGroup {
    groupName: string;
    items: MenuItem[];
    requiredRole?: string;
}

interface SidebarLayoutProps {
    menuData: MenuGroup[];
}

// --- STYLES DU DRAWER (Slate 900 avec accentuation Indigo) ---
const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: '#18192b',
    borderRight: '1px solid rgba(255,255,255,0.05)',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
    },
    backgroundColor: '#18192b',
    borderRight: 'none',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

/**
 * Barre latérale + zone de contenu (Outlet) génériques, réutilisées par chaque section
 * indépendante de l'application (Chiffrage Projets, Navette de paie, Matrice Régie, ...).
 * Chaque section reste indépendante : elle ne voit que son propre menuData, jamais celui
 * d'une autre section.
 */
export default function SidebarLayout({ menuData }: SidebarLayoutProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { width } = useWindowDimensions();
    const userRoles = useSelector((state: RootState) => state.user.roles);
    const hasUnsavedLotChanges = useSelector((state: RootState) => state.ui.hasUnsavedLotChanges);

    const visibleMenuData = menuData.filter((group) => !group.requiredRole || userRoles?.includes(group.requiredRole));

    const [open, setOpen] = React.useState(true);
    const [locked, setLocked] = React.useState(true);
    const [pendingPage, setPendingPage] = React.useState<string | null>(null);
    const mobile = width ? width < 900 : false;

    React.useEffect(() => {
        if (mobile) setOpen(false);
    }, [mobile]);

    const goToPage = (page: string) => {
        if (hasUnsavedLotChanges) {
            setPendingPage(page);
        } else {
            navigate(page);
        }
    };

    const confirmPendingPage = () => {
        if (pendingPage !== null) {
            dispatch(setUnsavedLotChanges(false));
            navigate(pendingPage);
            setPendingPage(null);
        }
    };

    const cancelPendingPage = () => setPendingPage(null);

    return (
        <Box sx={{ display: "flex", bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <CssBaseline />

            <Navbar onMenuClick={() => setOpen(!open)} blueLogoFilter />

            <Drawer variant={mobile ? "temporary" : "permanent"} open={open} onClose={() => setOpen(false)}>
                <DrawerHeader />
                <List sx={{ px: 1.5, mt: 1 }}>
                    {visibleMenuData.map((group) => (
                        <Box key={group.groupName} sx={{ mb: 2 }}>
                            {open && (
                                <ListSubheader sx={{
                                    bgcolor: "transparent",
                                    color: "rgba(255,255,255,0.35)",
                                    fontSize: 10,
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    letterSpacing: 1.2,
                                    mb: 0.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    {group.groupName}
                                    {group.groupName === "Projets" && (
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setLocked(!locked); }} sx={{ color: "rgba(255,255,255,0.2)" }}>
                                            {locked ? <LockIcon sx={{ fontSize: 14 }} /> : <LockOpenIcon sx={{ fontSize: 14 }} />}
                                        </IconButton>
                                    )}
                                </ListSubheader>
                            )}

                            {group.items.map((item) => {
                                const isActive = location.pathname === item.page;
                                return (
                                    <ListItem
                                        key={item.text}
                                        onClick={() => goToPage(item.page)}
                                        sx={{
                                            mb: 0.3,
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            py: 0.8,
                                            // Couleur de fond : Indigo transparent si actif
                                            bgcolor: isActive ? "rgba(99, 102, 241, 0.15)" : "transparent",
                                            // Couleur du texte : Indigo brillant si actif
                                            color: isActive ? "#818cf8" : "rgba(255,255,255,0.6)",
                                            "&:hover": {
                                                bgcolor: isActive ? "rgba(99, 102, 241, 0.25)" : "rgba(255,255,255,0.04)",
                                                color: "#fff"
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: 35,
                                            color: isActive ? "#818cf8" : "inherit",
                                            "& svg": { fontSize: 20 }
                                        }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        {open && (
                                            <ListItemText
                                                primary={item.text}
                                                primaryTypographyProps={{
                                                    fontSize: 13,
                                                    fontWeight: isActive ? 600 : 400,
                                                }}
                                            />
                                        )}
                                    </ListItem>
                                );
                            })}
                        </Box>
                    ))}
                </List>

                <Box sx={{ mt: 'auto', pb: 2, textAlign: 'center' }}>
                     {open && (
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.2)", fontSize: 9, fontWeight: 600, letterSpacing: 1 }}>
                            KAIROS PLATFORM v2.4
                        </Typography>
                     )}
                </Box>
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 3 },
                    width: "100%",
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <DrawerHeader />
                <Outlet />
            </Box>

            <AlerteDialog
                open={pendingPage !== null}
                handleClose={cancelPendingPage}
                deleteProject={confirmPendingPage}
                title="Modifications non enregistrées"
                contentText="Voulez-vous quitter sans enregistrer les modifications du lot ?"
            />
        </Box>
    );
}
